import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAllFileCategories } from "services/files-category.service";

interface Category {
  id: string;
  title: string;
  parentCategory: string | null;
}

const PATH_SEPARATOR = " › ";

/**
 * Where a document lands in the document tree, as the person reads it:
 * "Personalas › Įdarbinimas". Returns a function that turns a category id
 * into that path. The path is "…" until the company's categories have loaded,
 * and stays "…" for a category that no longer exists.
 */
export function useCategoryPaths(companyId: string | undefined) {
  const [categories, setCategories] = useState<Category[]>([]);
  const byId = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories]
  );

  useEffect(() => {
    if (!companyId) return;
    fetchAllFileCategories(companyId)
      .then((list: Category[]) => setCategories(list))
      .catch(() => setCategories([]));
  }, [companyId]);

  return useCallback(
    (categoryId: string): string => {
      const titles: string[] = [];
      const visited = new Set<string>();
      let current = byId.get(categoryId);
      // A broken parent chain (a category inside itself) must not loop forever.
      while (current && !visited.has(current.id)) {
        visited.add(current.id);
        titles.unshift(current.title);
        current = current.parentCategory
          ? byId.get(current.parentCategory)
          : undefined;
      }
      return titles.join(PATH_SEPARATOR) || "…";
    },
    [byId]
  );
}
